import { Component, OnInit, Input, ViewChild, ElementRef, ChangeDetectionStrategy, OnChanges, Renderer2 } from '@angular/core';
import * as d3 from 'd3';

import { AppHubUtilService } from '../../../shared';

@Component({
    selector: 'app-heat-map',
    templateUrl: './heat-map.component.html',
    styleUrls: ['./heat-map.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class HeatMapComponent implements OnInit, OnChanges {
  constructor(private renderer: Renderer2, private appUtilService: AppHubUtilService) { }

  @ViewChild('heatMap', { static: true }) private chartContainer: ElementRef;
  appCountPresent: boolean;

  @Input() private data: Array<any>;
  @Input() private checked: boolean;
  @Input() private configuration: {
    rowHeader: string;
    columnHeader: string;
    value: string;
    width: string;
    cellSize: string;
    margin: { top: number; bottom: number; left: number; right: number };
    cellClick:any;
    componentContext: any;
  };
  private margin: { top: number; bottom: number; left: number; right: number };
  // private height = this.configuration.height;
  // private width = this.configuration.width;

  ngOnInit() {
    this.margin = this.configuration.margin;
    this.renderHeatMap();
  }

  ngOnChanges() {
    if (this.margin) {
      this.removePlaceholderElements(this.chartContainer.nativeElement, this.chartContainer.nativeElement.querySelectorAll('svg'));
      this.removePlaceholderElements(this.chartContainer.nativeElement, this.chartContainer.nativeElement.querySelectorAll('div'));
      this.renderHeatMap();
    }
  }

  renderHeatMap() {
    let mousemove;
    const xElements = d3.set(this.data.map(item => item[this.configuration.rowHeader])).values();
    const yElements = d3.set(this.data.map(item => item[this.configuration.columnHeader])).values();
    const chrtWidth = this.chartContainer.nativeElement.offsetWidth - this.configuration.margin.left - this.configuration.margin.right;
    const itemWidth = Math.floor(chrtWidth / xElements.length ) ;
    const itemHeight = Math.floor(itemWidth / 2) + 7;
    const xScale = d3
      .scaleBand()
      .domain(xElements)
      .range([0, xElements.length * itemWidth]);

    const xAxis = d3.axisTop(xScale).tickFormat(d => d);

    const yScale = d3
      .scaleBand()
      .domain(yElements)
      .range([0, yElements.length * itemHeight]);

    const yAxis = d3.axisLeft(yScale).tickFormat(d => {
      return d;
    });

    // const colorScale = d3.scaleThreshold()
    // .domain([d3.max(this.data, (d) => d.value), 0])
    // .range();
    const colors = ['#ffffd9', '#edf8b1', '#c7e9b4', '#7fcdbb', '#41b6c4', '#1d91c0', '#225ea8', '#253494', '#081d58'];
    const textColors = ['#000000', '#ffffff'];
    const textColorScale = d3
      .scaleQuantile()
      .domain([0, 8, d3.max(this.data, d => d[this.configuration.value])])
      .range(textColors);
    const colorScale = d3
      .scaleQuantile()
      .domain([0, 8, d3.max(this.data, d => d[this.configuration.value])])
      .range(colors);


    const svg = d3
      .select(this.chartContainer.nativeElement)
      .append('svg')
      .attr('width', this.chartContainer.nativeElement.offsetWidth)
      .attr('height', +((yElements.length + 3) * itemHeight) + +this.margin.top + +this.margin.bottom)
      .append('g')
      .attr('transform', 'translate(' + this.margin.left + ',' + this.margin.top + ')');

    // create a tooltip
    const tooltip = d3
      .select(this.chartContainer.nativeElement)
      .append('div')
      .style('opacity', 0)
      .attr('class', 'tooltip')
      .style('background-color', 'white')
      .style('border', 'solid')
      .style('border-width', '2px')
      .style('border-radius', '5px')
      .style('padding', '5px');

    const mouseover = function() {
      tooltip.style('opacity', 1);
      tooltip.style('display', 'block');
      d3.select(this)
        .style('opacity', 1);
    };
    const valueKey = this.configuration.value;
    const rowHeader = this.configuration.rowHeader;
    const columnHeader = this.configuration.columnHeader;
    if (this.checked) {
      mousemove = function(d) {
        tooltip
          .html(
            `<div class="row"><div class="col">Application Count: ${d[valueKey]}</div></div>
                <div class="row"><div class="col">LOB : ${d[rowHeader]}</div></div>
                <div class="row"><div class="col">Capability : ${d[columnHeader]}</div></div>`
          )

          .style('display', 'block')
          .style('opacity', 1)
          .style('left', d3.mouse(this)[0] + 30 + 'px')
          .style('top', d3.mouse(this)[1] + 10 + 'px');
      };
    } else {
      mousemove = function(d) {

        tooltip
          .html(
            `<div class="row"><div class="col">Infra Cost: $ ${d[valueKey]}</div></div>
                <div class="row"><div class="col">LOB : ${d[rowHeader]}</div></div>
                <div class="row"><div class="col">Capability : ${d[columnHeader]}</div></div>`
          )

          .style('display', 'block')
          .style('opacity', 1)
          .style('left', d3.mouse(this)[0] + 30 + 'px')
          .style('top', d3.mouse(this)[1] + 10 + 'px');
      };
    }
    const mouseleave = function() {
      tooltip.style('display', 'none');
      tooltip.style('opacity', 0);
      d3.select(this)
        .style('stroke', 'none')
        .style('opacity', 0.8);
    };

    const cells = svg
      .selectAll('rect')
      .data(this.data)
      .enter()
      .append('g')
      .on('click', this.configuration.cellClick.bind(this.configuration.componentContext))
      .on('mouseover', mouseover)
      .on('mousemove', mousemove)
      .on('mouseleave', mouseleave);

    cells
      .append('rect')
      .attr('class', 'cell')
      .attr('width', itemWidth)
      .attr('height', itemHeight)
      .attr('y', d => yScale(d[this.configuration.columnHeader]) + 2)
      .attr('x', d => xScale(d[this.configuration.rowHeader]) + 2)
      .attr('rx', 1)
      .attr('ry', 1)
      .attr('class', 'bordered')
      .attr('cursor', 'pointer')
      .attr('fill', (d) => {
        if (d[this.configuration.value] === 0) {
          return '#DCDCDC';
        } else {
          return colorScale(d[this.configuration.value]);
        }
      });

    if (this.checked) {
      cells
        .append('text')
        .attr('y', d => yScale(d[this.configuration.columnHeader]) + 10)
        .attr('x', d => xScale(d[this.configuration.rowHeader]) + 10)
        .attr('dy', '.5em')
        .text((d) => {

          if (+d[this.configuration.value] !== 0) {
            return d[this.configuration.value];
          }
        })
        .attr('fill', d => textColorScale(d[this.configuration.value]));
    } else {
      cells
        .append('text')
        .attr('y', d => yScale(d[this.configuration.columnHeader]) + 10)
        .attr('x', d => xScale(d[this.configuration.rowHeader]) + 3)
        .attr('dy', '.5em')
        .attr('font-size' , '8px')
        .text((d) => {

          if (+d[this.configuration.value] !== 0) {
            return this.decimalFormatter(d[this.configuration.value], 0);
          }
        })
        .attr('fill', d => textColorScale(d[this.configuration.value]));
    }

    svg
      .append('g')
      .attr('class', 'y axis')
      .call(yAxis)
      .selectAll('text')
      .attr('font-weight', 'normal');

    svg
      .append('g')
      .attr('class', 'x axis')
      .call(xAxis)
      .selectAll('text')
      .attr('font-weight', 'normal')
      .style('text-anchor', 'start')
      .attr('dx', '.8em')
      .attr('dy', '.5em')
      .attr('transform', ()=> {
        return 'rotate(-65)';
      });

    const legend = svg
      .selectAll('.legend')
      .data([0].concat(colorScale.quantiles()), d => d)
      .enter()
      .append('g')
      .attr('class', 'legend');
    legend
      .append('rect')
      .attr('x', (_d, i) => itemWidth * 3 * i)
      .attr('y', +(yElements.length * itemHeight) + +this.margin.bottom)
      .attr('width', itemWidth * 3)
      .attr('height', +itemHeight)
      .style('fill', (_d, i) => colors[i]);




    legend
      .append('text')
      .attr('class', 'mono')
      .text(d => { if (d === 0) { return '≥ ' + (Math.round(d) + 1); } else { return '≥ ' + Math.round(d); } })
      .attr('x', (_d, i) => itemWidth * 3 * i)
      .attr('y', +(yElements.length * itemHeight) + +this.margin.bottom + +itemHeight + 20);


    legend.exit().remove();
  }

  removePlaceholderElements(nativeElement, nodes) {
    for (const node of nodes) {
      this.renderer.removeChild(nativeElement, node);
    }
  }

  decimalFormatter(digit, num) {
    return '$' + this.appUtilService.decimalFormatter(digit, num);
  }
}
